// Persisted across the form-submit round-trip: Kratos 303s back to
// /settings?flow=… and remounts the SPA, so component state alone loses the
// active tab. sessionStorage scopes to the auth-ui origin and clears on tab
// close — exactly the lifetime we want.

import { peekRecoveryJustCompleted } from '@utils/recovery-handoff.utils'
import { safeRead, safeRemove, safeWrite } from '@utils/session-storage.utils'

const ACTIVE_KEY = 'paretos.authui.settings.activeTab'
const SUBMITTED_KEY = 'paretos.authui.settings.submitted'

export type SettingsTab = 'profile' | 'password' | 'twoFactor'

const ALL_TABS: ReadonlyArray<SettingsTab> = ['profile', 'password', 'twoFactor']

const isSettingsTab = (raw: unknown): raw is SettingsTab =>
  typeof raw === 'string' && (ALL_TABS as ReadonlyArray<string>).includes(raw)

export const readActiveTab = (): SettingsTab => {
  // Recovery handoff wins over the stored tab: the user JUST completed a
  // recovery flow and is here to change their password. Any
  // `paretos.authui.settings.activeTab` value (set by an earlier transit
  // through /settings, e.g. during an OAuth round-trip) is stale relative
  // to that signal.
  //
  // Peek, don't consume — this runs inside a lazy useState initializer
  // which React 18 StrictMode invokes twice in dev. SettingsPage clears
  // the flag from a useEffect after the first commit.
  if (peekRecoveryJustCompleted()) {
    return 'password'
  }
  const raw = safeRead(ACTIVE_KEY)
  if (isSettingsTab(raw)) {
    return raw
  }
  return 'profile'
}

export const writeActiveTab = (tab: SettingsTab): void => {
  safeWrite(ACTIVE_KEY, tab)
}

// Records which tab's form just POSTed and against which flow id. Read on
// the next mount (after Kratos 303s back) to gate success UI:
//   - banner: only when submittedContext.tab === current activeTab
//   - sign-out CTA: only on password tab when password was the submitter
// `flow.ui.messages` carries the 1050001 success code at the flow level
// without telling us which method produced it — this pairing fills that gap.
export interface SubmittedContext {
  flowId: string
  tab: SettingsTab
}

// Validate at the shape boundary rather than trusting the `as Partial<…>`
// cast: sessionStorage is writable from any same-origin script, and a wider
// SubmittedContext (added fields, type drift) would silently slip past a
// cast-then-check pattern but fail this guard explicitly.
const parseSubmittedContext = (raw: string): SubmittedContext | null => {
  let parsed: unknown
  try {
    parsed = JSON.parse(raw)
  } catch {
    return null
  }
  if (typeof parsed !== 'object' || parsed === null) {
    return null
  }
  const obj = parsed as Record<string, unknown>
  if (typeof obj.flowId !== 'string' || obj.flowId === '') {
    return null
  }
  if (!isSettingsTab(obj.tab)) {
    return null
  }
  return { flowId: obj.flowId, tab: obj.tab }
}

export const readSubmittedContext = (): SubmittedContext | null => {
  const raw = safeRead(SUBMITTED_KEY)
  if (raw === null) {
    return null
  }
  return parseSubmittedContext(raw)
}

export const writeSubmittedContext = (ctx: SubmittedContext): void => {
  safeWrite(SUBMITTED_KEY, JSON.stringify(ctx))
}

export const clearSubmittedContext = (): void => {
  safeRemove(SUBMITTED_KEY)
}
